import React from "react";

interface PhaseStatus {
	phase: 1 | 2 | 3;
	status: "awarded" | "pending" | "forfeited";
}

interface PhaseProgressTimelineProps {
	phases: PhaseStatus[];
}

const getTextClass = (status: PhaseStatus["status"]) => {
	switch (status) {
		case "awarded":
			return "text-success-active";
		case "forfeited":
			return "text-error-active";
		default:
			return "text-primary-scrim";
	}
};

const getStrokeClass = (status: PhaseStatus["status"]) => {
	switch (status) {
		case "awarded":
			return "stroke-success-active";
		case "forfeited":
			return "stroke-error-active";
		default:
			return "stroke-primary-inactive";
	}
};

const getLineClasses = (phases: PhaseStatus[], phaseIndex: number) => {
	// `from-[length:0_4px]` / `to-[length:4px_8px]` use the CSS Color Level 4 two-position
	// stop syntax (`color start end`), exposed via Tailwind's `length:` arbitrary-value type
	// hint. Equivalent to: repeating-linear-gradient(90deg, color 0 4px, transparent 4px 8px).
	const dashedLineClasses =
		"bg-repeating-gradient-to-r from-primary-inactive from-[length:0_4px] to-transparent to-[length:4px_8px]";
	// If the next phase is pending, use a dashed line
	if (phases[phaseIndex + 1].status === "pending") {
		return dashedLineClasses;
	}
	// Otherwise color the line based on the status of the current phase
	switch (phases[phaseIndex].status) {
		case "awarded":
			return "bg-success-active";
		case "forfeited":
			return "bg-error-active";
		default:
			return dashedLineClasses;
	}
};

const getIcon = (status: PhaseStatus["status"]) => {
	if (status === "awarded") {
		return (
			<path
				d="M6 12l4 4 8-8"
				className="stroke-success-active"
				strokeWidth="2"
				fill="none"
			/>
		);
	}
	if (status === "forfeited") {
		return (
			<>
				<path d="M8 8l8 8" className="stroke-error-active" strokeWidth="2" />
				<path d="M16 8l-8 8" className="stroke-error-active" strokeWidth="2" />
			</>
		);
	}
	return null;
};

const PhaseProgressTimeline: React.FC<PhaseProgressTimelineProps> = ({
	phases
}) => {
	return (
		<div className="flex items-center gap-0 w-full">
			{phases.map((phase, index) => (
				<React.Fragment key={phase.phase}>
					<div className="flex flex-col items-center">
						<svg width="28" height="28" viewBox="0 0 24 24">
							<circle
								cx="12"
								cy="12"
								r="10"
								fill="none"
								className={getStrokeClass(phase.status)}
								strokeWidth="2"
								strokeDasharray={phase.status === "pending" ? "4 4" : "none"}
							/>
							{getIcon(phase.status)}
						</svg>
						<span className={`text-xs mt-1 ${getTextClass(phase.status)}`}>
							Phase {phase.phase}
						</span>
					</div>
					{index < phases.length - 1 && (
						<div
							className={`flex-grow h-0.5 mx-1 ${getLineClasses(phases, index)}`}
						/>
					)}
				</React.Fragment>
			))}
		</div>
	);
};

PhaseProgressTimeline.displayName = "PhaseProgressTimeline";

export default PhaseProgressTimeline;
export type { PhaseProgressTimelineProps, PhaseStatus };
